import type { ApplicationAnswers } from '@/types/application'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'
import { CheckboxField } from './CheckboxField'
import { LicenseFields } from './LicenseFields'
import { ReadOnlyInfoCard } from './ReadOnlyInfoCard'
import { TextField } from './TextField'

interface ApplicationLicenseManagementProps {
  answers: ApplicationAnswers
  onChange: (answers: ApplicationAnswers) => void
  /** From the Infrastructure step: the DB server (with the SQL instance, where one was asked for). */
  sqlServer: string
  /** The Main DB name, from the Customer ID. */
  defaultDb: string
  /** The TLS port entered above. */
  tcpPort: string
  /** The license key entered above. */
  productId: string
}

const none = (value: string) => (value === '' ? '—' : value)

/**
 * License management, on the application server: the details of the Traverse Global License Server Manager. The Site ID and the keys
 * (asked here, by the environment's type), a Profile ID, and the logins of the TRAVERSE and the membership database. The SQL server,
 * default database, TCP port and Product ID are not asked again: they are the ones already given, shown read-only.
 */
export function ApplicationLicenseManagement({ answers, onChange, sqlServer, defaultDb, tcpPort, productId }: ApplicationLicenseManagementProps) {
  const traverse = answers.traverseDatabase
  const membership = answers.membershipDatabase
  const set = (patch: Partial<ApplicationAnswers>) => onChange({ ...answers, ...patch })
  const membershipUsesOwnLogin = !membership.useMasterLogin && !membership.useTraverseCredentials

  return (
    <section className={styles.section} aria-label="License management" data-testid="license-management">
      <h4 className={styles.sectionTitle}>License management</h4>
      <p className={styles.note}>The license server on the application server (Traverse Global License Server Manager).</p>

      <LicenseFields />

      <div className={page.formGrid} style={{ marginTop: 16 }}>
        <TextField id="license-profile-id" label="Profile ID" required value={answers.profileId} onChange={(profileId) => set({ profileId })} autoComplete="off" hint="The license server profile for this customer." />
      </div>

      <h5 className={styles.readOnlyTitle} style={{ marginTop: 16 }}>
        TRAVERSE Database
      </h5>
      <ReadOnlyInfoCard title="TRAVERSE Database server" items={[{ label: 'Sql Server', value: none(sqlServer), mono: true }]} note="The DB server you chose on the Infrastructure step." />
      <CheckboxField
        label="Use Master Login"
        checked={traverse.useMasterLogin}
        onChange={(useMasterLogin) => set({ traverseDatabase: { ...traverse, useMasterLogin, ...(useMasterLogin ? { login: '', password: '' } : {}) } })}
      />
      {!traverse.useMasterLogin && (
        <div className={page.formGrid}>
          <TextField id="license-traverse-login" label="Login" required value={traverse.login} onChange={(login) => set({ traverseDatabase: { ...traverse, login } })} autoComplete="off" />
          <TextField
            id="license-traverse-password"
            label="Password"
            required
            type="password"
            value={traverse.password}
            onChange={(password) => set({ traverseDatabase: { ...traverse, password } })}
            autoComplete="new-password"
            hint="Kept in Key Vault, never stored with the deployment."
          />
        </div>
      )}

      <h5 className={styles.readOnlyTitle} style={{ marginTop: 16 }}>
        Membership Database
      </h5>
      <ReadOnlyInfoCard title="Membership Database server" items={[{ label: 'Sql Server', value: none(sqlServer), mono: true }]} note="The same DB server." />
      <CheckboxField
        label="Use Master Login"
        checked={membership.useMasterLogin}
        onChange={(useMasterLogin) =>
          set({ membershipDatabase: { ...membership, useMasterLogin, ...(useMasterLogin ? { useTraverseCredentials: false, login: '', password: '' } : {}) } })
        }
      />
      <CheckboxField
        label="Use TRAVERSE Credentials"
        checked={membership.useTraverseCredentials}
        onChange={(useTraverseCredentials) =>
          set({ membershipDatabase: { ...membership, useTraverseCredentials, ...(useTraverseCredentials ? { useMasterLogin: false, login: '', password: '' } : {}) } })
        }
      />
      {membershipUsesOwnLogin && (
        <div className={page.formGrid}>
          <TextField id="license-membership-login" label="Login" required value={membership.login} onChange={(login) => set({ membershipDatabase: { ...membership, login } })} autoComplete="off" />
          <TextField
            id="license-membership-password"
            label="Password"
            required
            type="password"
            value={membership.password}
            onChange={(password) => set({ membershipDatabase: { ...membership, password } })}
            autoComplete="new-password"
            hint="Kept in Key Vault, never stored with the deployment."
          />
        </div>
      )}

      <ReadOnlyInfoCard
        title="Others"
        items={[
          { label: 'Default DB', value: none(defaultDb), mono: true },
          { label: 'Tcp Port', value: none(tcpPort), mono: true },
          { label: 'Product ID', value: none(productId), mono: true },
        ]}
        note="Default DB is the Main DB name, Tcp Port the TLS port, and Product ID the license key, as you entered them."
      />
    </section>
  )
}
